import { HankoDialog } from "../../../../components/HankoDialog";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import React, { useContext, useEffect } from "react";
import CopyableTextField from "../../../../components/CopyableTextField";
import { ApiClient } from "../../../../client/ApiClient";
import { Project } from "../../../../models/Project";
import { FormButtons } from "../../../../components/form/FormButtons";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { useRevalidator } from "react-router-dom";
import { HankoHelpTooltip } from "../../../../components/Tooltip/Tooltip";
import { ClaimMappingEditor } from "../../../../components/form/ClaimMappingEditor";
import { isStandardAttributeMapEmpty, oidcAttributeMapHelperText, oidcStandardAttributeFields, StandardAttributeMapFields } from "../../../../components/form/StandardAttributeMapFields";

interface Props {
  open: boolean
  onClose: () => void
  organizationId: string
  project: Project
  id?: string
}

type State = {
  connection_name?: string
  client_id?: string
  client_secret?: string
  use_discovery?: boolean
  issuer?: string
  authorization_endpoint?: string
  token_endpoint?: string
  userinfo_endpoint?: string
  scopes?: string
  allow_linking: boolean
  prompt?: string
  acr_values?: string
  attribute_mapping: Record<string, string>
  custom_claim_mapping: Record<string, string>
}

const emptyState: State = {
  use_discovery: true,
  allow_linking: false,
  attribute_mapping: {},
  custom_claim_mapping: {},
}

export const CustomProviderModal = (props: Props) => {
  const { id, open, onClose, organizationId, project } = props
  const [isLoading, setIsLoading] = React.useState(false)
  const revalidator = useRevalidator()
  const { error } = useContext(SnackbarContext)
  const [state, setState] = React.useState<State>(emptyState)

  useEffect(() => {
    if (id !== undefined && project.third_party.custom_providers !== undefined && project.third_party.custom_providers[id] !== undefined) {
      const provider = project.third_party.custom_providers[id]
      setState({
        allow_linking: provider.allow_linking ?? false,
        scopes: provider.scopes?.join(", "),
        client_id: provider.client_id,
        client_secret: provider.secret,
        connection_name: provider.display_name,
        issuer: provider.issuer,
        use_discovery: provider.use_discovery,
        token_endpoint: provider.token_endpoint,
        authorization_endpoint: provider.authorization_endpoint,
        userinfo_endpoint: provider.userinfo_endpoint,
        prompt: provider.prompt,
        acr_values: provider.acr_values?.join(", "),
        attribute_mapping: provider.attribute_mapping ?? {},
        custom_claim_mapping: project.custom_provider_claim_mappings?.[id] ?? {},
      })
    }
  }, [id]);

  const submit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const providerId = id ?? crypto.randomUUID()
      const customProviders = project.third_party.custom_providers ?? {}
      customProviders[providerId] = {
        allow_linking: state.allow_linking,
        attribute_mapping: isStandardAttributeMapEmpty(oidcStandardAttributeFields, state.attribute_mapping) ? undefined : state.attribute_mapping,
        scopes: state.scopes?.split(',').map(v => v.trim()).filter(Boolean),
        client_id: state.client_id ?? "",
        secret: state.client_secret ?? "",
        display_name: state.connection_name ?? "",
        enabled: customProviders[providerId]?.enabled ?? true,
        issuer: state.issuer ?? "",
        use_discovery: state.use_discovery ?? true,
        token_endpoint: state.token_endpoint,
        authorization_endpoint: state.authorization_endpoint,
        userinfo_endpoint: state.userinfo_endpoint,
        prompt: state.prompt,
        acr_values: state.acr_values?.split(',').map(v => v.trim()).filter(Boolean),
      }

      if (id !== undefined && project.third_party.custom_providers !== undefined && project.third_party.custom_providers[id] !== undefined) {
        customProviders[providerId].enabled = project.third_party.custom_providers[id].enabled
      }

      const customProviderClaimMappings = { ...project.custom_provider_claim_mappings }
      if (Object.keys(state.custom_claim_mapping).length > 0) {
        customProviderClaimMappings[providerId] = state.custom_claim_mapping
      } else {
        delete customProviderClaimMappings[providerId]
      }

      await ApiClient.updateProject(organizationId, project.id, {
        third_party: {
          ...project.third_party,
          custom_providers: customProviders,
        },
        custom_provider_claim_mappings: customProviderClaimMappings,
      })
      revalidator.revalidate()
      closeModal()
    } catch (e) {
      if (e instanceof Response && e.status === 400) {
        const errorResponse = await e.json()
        error(errorResponse.message ?? "Failed to create connection")
      } else {
        error("Failed to create connection")
        console.log(e)
      }
    }
    setIsLoading(false)
  }

  const closeModal = () => {
    setState(emptyState)
    onClose()
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  const onChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.checked
    })
  }

  return <HankoDialog open={open} onClose={onClose} fullScreen>
    <Box maxWidth="500px" margin="auto">
    <form onSubmit={submit}>
      <Box maxWidth="500px" sx={{ display: "flex", gap: "20px", flexDirection: 'column' }}>
        <Typography variant="h4">Custom SSO connection</Typography>
        <p>Create a custom SSO connection using OAuth or OpenID Connect.</p>
        <CopyableTextField copyableContent={project.api_url + "/thirdparty/callback"}
                           label="Redirect URL (read only)" />
        <TextField label="Connection name" name="connection_name" fullWidth onChange={onChange} required value={state.connection_name} />
        <TextField label="Client ID" name="client_id" fullWidth onChange={onChange} required value={state.client_id} />
        <TextField label="Client secret" name="client_secret" fullWidth onChange={onChange} required autoComplete="off" value={state.client_secret} />
        <TextField label="Scopes" name="scopes" fullWidth onChange={onChange} required value={state.scopes}
          InputProps={{
           endAdornment: <InputAdornment position="end">
             <HankoHelpTooltip title="A comma separated list of scopes to request with the provider (whitespace between
             commas and items is ignored)." />
           </InputAdornment>,
          }}
        />
        <TextField label="Prompt" name="prompt" fullWidth onChange={onChange} value={state.prompt} InputProps={{
          endAdornment: <InputAdornment position="end">
            <HankoHelpTooltip
              title="The prompt parameter specifies how the authorization server should interact with the end-user. 
              Common values include: 'none' (no interaction), 'login' (force re-authentication), 'consent' (force consent), 
              or 'select_account' (prompt to select account). If left empty, the default behavior of the authorization server will be used." />
          </InputAdornment>,
        }} />
        <TextField label="ACR values" name="acr_values" fullWidth onChange={onChange} value={state.acr_values}
                   InputProps={{
                     endAdornment: <InputAdornment position="end">
                       <HankoHelpTooltip title="A comma separated list of acr values to request with the provider (whitespace between
             commas and items is ignored)." />
                     </InputAdornment>,
                   }}
        />
        <FormGroup>
          <FormControlLabel control={<Checkbox onChange={onChecked} name="allow_linking" checked={state.allow_linking} />}
                            label="Allow Linking" />
        </FormGroup>
        <FormGroup>
          <FormControlLabel control={<Checkbox onChange={onChecked} name="use_discovery" checked={state.use_discovery} />}
                            label="Use OIDC discovery" />
        </FormGroup>
        {state.use_discovery === true ?
        <>
          <TextField type="url" label="Issuer" name="issuer" fullWidth onChange={onChange}
             required={state.use_discovery} value={state.issuer}
             InputProps={{
               endAdornment: <InputAdornment position="end">
                 <HankoHelpTooltip title="The issuer is the URL identifier for the service without the '/.well-known/openid-configuration' path suffix.
                    OpenID Connect providers that don't implement discovery or host the discovery
                    document at a non-spec complaint path, should disable usage of discovery and provide endpoint URLs manually instead." />
               </InputAdornment>,
             }}/>
        </>
          :
        <>
          <TextField type="url" label="Authorization endpoint" name="authorization_endpoint" fullWidth onChange={onChange}
                     required={state.use_discovery === false} value={state.authorization_endpoint} />
          <TextField type="url" label="Token endpoint" name="token_endpoint" fullWidth onChange={onChange}
                     required={state.use_discovery === false} value={state.token_endpoint} />
          <TextField type="url" label="Userinfo endpoint" name="userinfo_endpoint" fullWidth onChange={onChange}
                     required={state.use_discovery === false} value={state.userinfo_endpoint} />
        </>}

        <StandardAttributeMapFields fields={oidcStandardAttributeFields} helperText={oidcAttributeMapHelperText}
                                    value={state.attribute_mapping}
                                    onChange={(attribute_mapping) => setState({ ...state, attribute_mapping })} />
        <ClaimMappingEditor label="Custom claim mapping" valueLabel="Provider claim"
                            helperText={<>Map this provider's own claims onto your tenant's declared custom claims.
                              The value is a <Link href="https://github.com/tidwall/gjson#path-syntax" target="_blank"
                              rel="noopener noreferrer">gjson path</Link> evaluated against the provider's raw claims
                              - e.g. <strong>department</strong> for a top-level claim, <strong>address.city</strong> for
                              a nested field, or <strong>groups.0</strong> for the first entry in a list.</>}
                            valueHelpText={<>Evaluated against this provider's raw claims as a <Link
                              href="https://github.com/tidwall/gjson#path-syntax" target="_blank"
                              rel="noopener noreferrer">gjson path</Link>. Examples: <strong>department</strong> reads a
                              top-level claim; <strong>address.city</strong> reads a nested field;
                              <strong>groups.0</strong> reads the first entry in a list; <strong>groups.#</strong> counts
                              a list's entries.</>}
                            value={state.custom_claim_mapping}
                            onChange={(custom_claim_mapping) => setState({ ...state, custom_claim_mapping })}
                            knownClaimNames={Object.keys(project.custom_claims ?? {})} />
        <FormButtons onCancel={() => closeModal()} loading={isLoading} disabled={false} submitButtonText={id !== undefined ? "Save" : "Add connection"} />
      </Box>
    </form>
    </Box>
  </HankoDialog>
}